import { useEffect } from "react";
import {
    Container,
    Typography,
    Box,
    Button,
    TextField,
    FormControl,
    FormLabel,
    MenuItem,
    FormControlLabel,
    Switch,
    Stack,
    Divider,
    Paper,
    useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { URLHandler } from "src/services/URLHandler";
import { TagContext, TAG_CONTEXTS } from "src/types/tag";
import { useGetOneTagType, useCreateTagType, useUpdateTagType } from "src/api/client-api/tag";
import { useQueryClient } from "react-query";
import { useSnackbar } from "src/utils/useSnackbar";
import { Loading } from "src/components/utils/Loading";

// Schéma de validation
const TagTypeFormSchema = z.object({
    name: z.string().min(1, "Le nom est obligatoire").max(255, "Le nom ne peut pas dépasser 255 caractères"),
    description: z.string().optional(),
    context: z.enum(["global", "product", "ingredient", "cms", "supplier", "organization", "packaging"]),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "La couleur doit être au format hexadécimal (#RRGGBB)"),
    multiple: z.boolean(),
    required: z.boolean(),
    active: z.boolean(),
    sortOrder: z.number().min(0, "L'ordre de tri doit être positif"),
});

type TagTypeFormData = z.infer<typeof TagTypeFormSchema>;

interface TagTypeFormProps {
    mode: "create" | "edit";
}

export const TagTypeForm = ({ mode }: TagTypeFormProps) => {
    const { tagTypeId } = useParams<{ tagTypeId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const snackbar = useSnackbar();
    // const [submitError, setSubmitError] = useState<string | null>(null);

    // Récupération des données en mode édition
    const { data: tagType, isLoading } = useGetOneTagType(mode === "edit" ? tagTypeId : undefined);

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        watch,
    } = useForm<TagTypeFormData>({
        resolver: zodResolver(TagTypeFormSchema),
        defaultValues: {
            name: "",
            description: "",
            context: "global",
            color: "#6366f1",
            multiple: true,
            required: false,
            active: true,
            sortOrder: 0,
        },
    });

    // Mise à jour du formulaire en mode édition
    useEffect(() => {
        if (mode === "edit" && tagType) {
            reset({
                name: tagType.name,
                description: tagType.description || "",
                context: tagType.context,
                color: tagType.color,
                multiple: tagType.multiple,
                required: tagType.required,
                active: tagType.active,
                sortOrder: tagType.sortOrder,
            });
        }
    }, [tagType, mode, reset]);

    // Mutations
    const createMutation = useCreateTagType();
    const updateMutation = useUpdateTagType();

    const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

    const onSubmit = (data: TagTypeFormData) => {
        if (mode === "create") {
            createMutation.mutate(data, {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        message: "Type de tag créé avec succès",
                        type: "success",
                    });
                    navigate(URLHandler.settings.tags.tagTypes.list());
                },
                onError: () => {
                    const errorMessage = t("tags.messages.failed_to_create_tag_type", "Failed to create tag type");
                    snackbar.setSnackbarProps({
                        message: errorMessage,
                        type: "error",
                    });
                },
            });
        } else if (mode === "edit" && tagTypeId) {
            updateMutation.mutate(
                { id: tagTypeId, data },
                {
                    onSuccess: () => {
                        snackbar.setSnackbarProps({
                            message: "Type de tag mis à jour avec succès",
                            type: "success",
                        });
                        navigate(URLHandler.settings.tags.tagTypes.list());
                    },
                    onError: () => {
                        const errorMessage = t("tags.messages.failed_to_update_tag_type", "Failed to update tag type");
                        snackbar.setSnackbarProps({
                            message: errorMessage,
                            type: "error",
                        });
                    },
                },
            );
        }
    };

    const contextLabels: Record<TagContext, string> = {
        global: "Global",
        product: "Produit",
        ingredient: "Ingrédient",
        cms: "CMS",
        supplier: "Fournisseur",
        organization: "Entreprise",
        packaging: "Packaging",
    };

    if (mode === "edit" && isLoading) {
        return <Loading />;
    }

    const allColorsPreset = theme.custom.dataviz.onlyMainFromAll;
    return (
        <Container
            component="main"
            maxWidth="lg"
            sx={{
                py: 4,
                px: 2,
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography component="h1" variant="h2">
                    {mode === "create" ?
                        t("tags.form.create_tag_type", "Créer un type de tag")
                    :   t("tags.form.edit_tag_type", "Modifier un type de tag")}
                </Typography>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            <Paper sx={{ p: 4, maxWidth: 800 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={3}>
                        {/* Nom */}
                        <FormControl>
                            <FormLabel>{t("tags.form.fields.name", "Nom *")}</FormLabel>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        placeholder={t("tags.form.fields.name_placeholder", "Nom du type de tag")}
                                        fullWidth
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: (theme) => theme.palette.primary[600],
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormControl>

                        {/* Description */}
                        <FormControl>
                            <FormLabel>{t("tags.form.fields.description", "Description")}</FormLabel>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        multiline
                                        rows={3}
                                        error={!!errors.description}
                                        helperText={errors.description?.message}
                                        placeholder={t(
                                            "tags.form.fields.description_placeholder",
                                            "Description du type de tag",
                                        )}
                                        fullWidth
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: (theme) => theme.palette.primary[600],
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormControl>

                        {/* Contexte */}
                        <FormControl>
                            <FormLabel>{t("tags.form.fields.context", "Contexte *")}</FormLabel>
                            <Controller
                                name="context"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        error={!!errors.context}
                                        helperText={
                                            errors.context?.message ||
                                            t(
                                                "tags.form.fields.context_helper_text",
                                                "Détermine où ce type de tag peut être utilisé",
                                            )
                                        }
                                        fullWidth
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: (theme) => theme.palette.primary[600],
                                                },
                                            },
                                        }}
                                    >
                                        {TAG_CONTEXTS.map((context) => (
                                            <MenuItem key={context} value={context}>
                                                {contextLabels[context]}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </FormControl>

                        {/* Couleur */}
                        <FormControl>
                            <FormLabel>{t("tags.form.fields.color", "Couleur *")}</FormLabel>
                            <Stack gap={theme.spacing(2)}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Controller
                                        name="color"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="color"
                                                error={!!errors.color}
                                                helperText={errors.color?.message}
                                                sx={{
                                                    width: 100,
                                                    "& .MuiOutlinedInput-root": {
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: (theme) => theme.palette.primary[600],
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="color"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                placeholder="#6366f1"
                                                error={!!errors.color}
                                                sx={{
                                                    width: 150,
                                                    "& .MuiOutlinedInput-root": {
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: (theme) => theme.palette.primary[600],
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            backgroundColor: watch("color"),
                                            border: "1px solid #ccc",
                                            borderRadius: 1,
                                        }}
                                    />
                                </Stack>
                                <Controller
                                    name="color"
                                    control={control}
                                    render={({ field }) => {
                                        return (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                {allColorsPreset.map((color) => {
                                                    return (
                                                        <Box
                                                            key={color}
                                                            sx={{
                                                                width: "20px",
                                                                height: "20px",
                                                                backgroundColor: color,
                                                                border: "1px solid #ccc",
                                                                borderRadius: 12,
                                                            }}
                                                            onClick={() => {
                                                                field.onChange(color);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                        );
                                    }}
                                />
                            </Stack>
                        </FormControl>

                        {/* Ordre de tri */}
                        <FormControl>
                            <FormLabel>{t("tags.form.fields.sort_order", "Ordre de tri")}</FormLabel>
                            <Controller
                                name="sortOrder"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        error={!!errors.sortOrder}
                                        helperText={
                                            errors.sortOrder?.message ||
                                            t(
                                                "tags.form.fields.sort_order_helper_text",
                                                "Ordre d'affichage (plus petit = plus haut)",
                                            )
                                        }
                                        inputProps={{ min: 0 }}
                                        sx={{
                                            width: 200,
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: (theme) => theme.palette.primary[600],
                                                },
                                            },
                                        }}
                                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                    />
                                )}
                            />
                        </FormControl>

                        {/* Options */}
                        <Stack spacing={2}>
                            <Typography variant="h6">{t("tags.form.fields.options", "Options")}</Typography>

                            <FormControlLabel
                                control={
                                    <Controller
                                        name="multiple"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <Switch
                                                checked={value}
                                                onChange={(e) => onChange(e.target.checked)}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: (theme) => theme.palette.primary[600],
                                                    },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: (theme) => theme.palette.primary[600],
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                }
                                label={t("tags.form.fields.multiple_values_allowed", "Valeurs multiples autorisées")}
                            />

                            <FormControlLabel
                                control={
                                    <Controller
                                        name="required"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <Switch
                                                checked={value}
                                                onChange={(e) => onChange(e.target.checked)}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: (theme) => theme.palette.primary[600],
                                                    },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: (theme) => theme.palette.primary[600],
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                }
                                label={t("tags.form.fields.required_tag_type", "Type de tag obligatoire")}
                            />

                            <FormControlLabel
                                control={
                                    <Controller
                                        name="active"
                                        control={control}
                                        render={({ field: { value, onChange } }) => (
                                            <Switch
                                                checked={value}
                                                onChange={(e) => onChange(e.target.checked)}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                        color: (theme) => theme.palette.primary[600],
                                                    },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: (theme) => theme.palette.primary[600],
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                }
                                label={t("tags.form.fields.active", "Actif")}
                            />
                        </Stack>

                        {/* Boutons d'action */}
                        <Stack direction="row" spacing={2} sx={{ pt: 2, justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(URLHandler.settings.tags.tagTypes.list())}
                                sx={{
                                    border: (theme) => `1px solid ${theme.palette.primary[600]}`,
                                    color: (theme) => theme.palette.primary[600],
                                    textTransform: "none",
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    "&:hover": {
                                        border: (theme) => `1px solid ${theme.palette.primary[700]}`,
                                        color: (theme) => theme.palette.primary[700],
                                        backgroundColor: "transparent",
                                    },
                                }}
                            >
                                {t("common.actions.cancel", "Annuler")}
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={isSubmitting || !isDirty}
                                sx={(theme) => ({
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: "white !important",
                                    textTransform: "none",
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    lineHeight: "1.5em",
                                    boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    },
                                    "&:disabled": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        opacity: 0.36,
                                        color: "white !important",
                                    },
                                })}
                            >
                                {isSubmitting ?
                                    t("common.actions.saving", "Enregistrement...")
                                :   t("common.actions.save", "Enregistrer")}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Paper>
        </Container>
    );
};
